/**
 * Job feed service
 * -----------------------------------------------------------------------------
 * Fetches job listings directly from WordPress's native REST API. This is a
 * first-party, CORS-enabled endpoint on the same domain that owns the data —
 * no third-party proxy (rss2json / corsproxy) and no XML parsing required.
 *
 * The `_embed` parameter includes taxonomy terms (job types) inline under
 * `_embedded['wp:term']`, so job-type labels resolve in a single request.
 *
 * Location and remote flag come from structured post meta. Clearance and salary
 * range are NOT reliably in meta (meta._job_salary is empty on every listing),
 * so they are parsed out of content.rendered, which consistently starts with:
 *   <p><strong>Location:</strong> ...</p>
 *   <p><strong>Clearance:</strong> ...</p>
 *   ... and includes a line beginning "Salary Range:" further down.
 * Any field whose pattern isn't found is gracefully omitted.
 */
const API_URL = 'https://jobs.newriversystems.com/wp-json/wp/v2/job-listings?_embed'
export interface Job {
  id: string
  title: string
  link: string
  location: string
  remote: boolean
  jobType: string
  clearance: string
  salaryRange: string
  applyEmail: string
  publishDate: string
  /** Full description as plain text (HTML stripped) — used for previews. */
  description: string
  /** Full description as formatted HTML — used in the expanded view. */
  descriptionHtml: string
}
interface WpRendered {
  rendered?: string
}
interface WpJobListing {
  id?: number
  date?: string
  link?: string
  title?: WpRendered
  content?: WpRendered
  meta?: {
    _job_location?: string
    _remote_position?: string | number
    _application?: string
    _job_salary?: string
    [key: string]: unknown
  }
  _embedded?: {
    'wp:term'?: Array<Array<{ name?: string; taxonomy?: string }>>
    [key: string]: unknown
  }
  [key: string]: unknown
}
/** Strip HTML tags and collapse whitespace/entities into readable plain text. */
function stripHtml(html: string): string {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc
    .querySelectorAll('br')
    .forEach((br) => br.replaceWith(document.createTextNode('\n')))
  doc
    .querySelectorAll('p, li, div, h1, h2, h3, h4, tr')
    .forEach((el) => el.append(document.createTextNode('\n')))
  const text = doc.body.textContent || ''
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n')
    .trim()
}
/**
 * Best-effort extraction of a labelled value from plain text (HTML stripped).
 * Handles "Label: value" on one line, or "Label" then value on the next line.
 * Returns '' if not found so callers can gracefully omit the field.
 */
function extractLabelled(text: string, labels: string[]): string {
  const lines = text.split('\n')
  for (const label of labels) {
    const inline = new RegExp(`${label}\\s*[:\\-–]\\s*(.+)`, 'i')
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(inline)
      if (m && m[1].trim()) return m[1].trim()
      if (new RegExp(`^${label}\\s*[:\\-–]?\\s*$`, 'i').test(lines[i])) {
        const next = lines[i + 1]?.trim()
        if (next) return next
      }
    }
  }
  return ''
}
/** Flatten _embedded['wp:term'] and return the first readable term name. */
function resolveJobType(item: WpJobListing): string {
  const groups = item._embedded?.['wp:term']
  if (!Array.isArray(groups)) return ''
  for (const group of groups) {
    if (!Array.isArray(group)) continue
    for (const term of group) {
      if (term && typeof term.name === 'string' && term.name.trim()) {
        return term.name.trim()
      }
    }
  }
  return ''
}
export async function fetchJobs(): Promise<Job[]> {
  const res = await fetch(API_URL, {
    // Always hit the network so new postings/closures appear without a rebuild.
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  const data: WpJobListing[] = await res.json()
  if (!Array.isArray(data)) {
    throw new Error('Unexpected response shape from job listings API')
  }
  const jobs = data.map((item, i): Job => {
    const title = (item.title?.rendered || '').trim() || 'Untitled Position'
    const link = (item.link || '').trim()
    const publishDate = (item.date || '').trim()
    const html = item.content?.rendered || ''
    const description = stripHtml(html)
    const location =
      (item.meta?._job_location || '').trim() ||
      extractLabelled(description, ['Location']) ||
      'Not specified'
    const remote = String(item.meta?._remote_position ?? '').trim() === '1'
    const applyEmail = (item.meta?._application || '').trim()
    const jobType = resolveJobType(item)
    const clearance = extractLabelled(description, [
      'Clearance',
      'Security Clearance',
    ])
    const salaryRange = extractLabelled(description, [
      'Salary Range',
      'Salary',
      'Compensation',
      'Pay Range',
    ])
    return {
      id: item.id ? String(item.id) : link || `${title}-${i}`,
      title,
      link,
      location,
      remote,
      jobType,
      clearance,
      salaryRange,
      applyEmail,
      publishDate,
      description,
      descriptionHtml: html,
    }
  })
  return jobs
}
